import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import ProductCard from '../components/common/ProductCard';
import storeApi from '../services/storeApi';

/**
 * WishlistPage - Wishlist page
 * Follows React Rules: Side effects in useEffect, proper state management
 */
const WishlistPage = () => {
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [error, setError] = useState(null);

  // Fetch wishlist
  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await storeApi.getWishlist();
        
        // Handle different response formats
        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (data && typeof data === 'object') {
          // Handle paginated response
          items = data.results || data.items || [];
        }
        
        // Ensure each item has a product field
        items = items.filter(item => item && item.product);
        setWishlist(items);
      } catch (err) {
        console.error('Error fetching wishlist:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          statusText: err.response?.statusText
        });
        
        // Check if user is authenticated
        const token = localStorage.getItem('access_token');
        if (!token) {
          setError('Please login to view your wishlist');
          return;
        }
        
        if (err.response?.status === 401 || err.response?.status === 403) {
          setError('Please login to view your wishlist. Your session may have expired.');
        } else if (err.response?.status === 404) {
          // API endpoint might not exist or user has no wishlist
          setWishlist([]);
          setError(null); // No error, just empty wishlist
        } else if (err.response?.status >= 500) {
          setError('Server error. Please try again later.');
        } else if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
          setError('Network error. Please check your connection and try again.');
        } else {
          // Show more detailed error message if available
          const errorMessage = err.response?.data?.detail || 
                             err.response?.data?.message || 
                             err.message || 
                             'Failed to load wishlist. Please try again.';
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWishlist();
  }, []);

  const handleRemoveFromWishlist = async (wishlistItemId) => {
    try {
      await storeApi.removeFromWishlist(wishlistItemId);
      setWishlist(prev => prev.filter(item => item.id !== wishlistItemId));
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      alert('Failed to remove item from wishlist');
    }
  };

  const handleAddToCart = async (productId) => {
    try {
      await storeApi.addToCart(productId, 1);
      // Trigger cart update event for header
      window.dispatchEvent(new Event('cartUpdated'));
      alert('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        alert('Please login to add items to cart');
      } else {
        alert('Failed to add product to cart. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p>Loading wishlist...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="wishlist-area pt-60 pb-60">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="text-center py-5">
                  <p className="text-danger mb-3">{error}</p>
                  {error.includes('login') && (
                    <Link to="/store/login-register" className="btn mr-3">Login</Link>
                  )}
                  <Link to="/store/shop" className="btn">Continue Shopping</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (wishlist.length === 0) {
    return (
      <PageContainer>
        <div className="wishlist-area pt-60 pb-60">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="wishlist-empty text-center">
                  <h2>Your wishlist is empty</h2>
                  <p>Add some products to your wishlist to save them for later.</p>
                  <Link to="/store/shop" className="btn">Continue Shopping</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="wishlist-area pt-60 pb-60">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="wishlist-table-content">
                <div className="table-content table-responsive">
                  <table>
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Product Name</th>
                        <th>Price</th>
                        <th>Stock Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wishlist.map((item) => {
                        // Safely access product data
                        const product = item.product || {};
                        const productId = product.id || item.product_id;
                        const productSlug = product.slug || '';
                        const productName = product.name || 'Unknown Product';
                        const productPrice = product.price || 0;
                        const productImage = product.primary_image_url || product.image_url || '/store/assets/images/product/large-size/1.jpg';
                        const isInStock = product.is_in_stock !== undefined ? product.is_in_stock : true;
                        
                        return (
                          <tr key={item.id}>
                            <td className="product-thumbnail">
                              {productSlug ? (
                                <Link to={`/store/product/${productSlug}`}>
                                  <img
                                    src={productImage}
                                    alt={productName}
                                    onError={(e) => {
                                      e.target.src = '/store/assets/images/product/large-size/1.jpg';
                                    }}
                                  />
                                </Link>
                              ) : (
                                <img
                                  src={productImage}
                                  alt={productName}
                                  onError={(e) => {
                                    e.target.src = '/store/assets/images/product/large-size/1.jpg';
                                  }}
                                />
                              )}
                            </td>
                            <td className="product-name">
                              {productSlug ? (
                                <Link to={`/store/product/${productSlug}`}>
                                  {productName}
                                </Link>
                              ) : (
                                <span>{productName}</span>
                              )}
                            </td>
                            <td className="product-price">
                              <span>PKR {parseFloat(productPrice).toFixed(2)}</span>
                            </td>
                            <td className="stock-status">
                              {isInStock ? (
                                <span className="text-success">In Stock</span>
                              ) : (
                                <span className="text-danger">Out of Stock</span>
                              )}
                            </td>
                            <td className="product-action">
                              {productId && (
                                <>
                                  <button
                                    className="btn"
                                    onClick={() => handleAddToCart(productId)}
                                    disabled={!isInStock}
                                  >
                                    Add to Cart
                                  </button>
                                  <button
                                    className="btn remove-btn"
                                    onClick={() => handleRemoveFromWishlist(item.id)}
                                  >
                                    <i className="fa fa-times"></i> Remove
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default WishlistPage;
